import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";

// ---------- Palette (matches site) ----------
const BG = "#0A0A0A";
const CARD = "#111114";
const BORDER = "rgba(201,169,97,0.20)";
const GOLD = "#C9A961";
const SLATE = "rgba(255,255,255,0.60)";

const STORAGE_KEY = "gvg_admin_token";

/* ============================================================
   Root
   ============================================================ */
export default function Admin() {
  usePageTitle("Admin");

  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("publish");

  // On mount, if a token is stored, quietly verify it against /api/leads.
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/leads`, { headers: { "X-Admin-Token": token } })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
        setToken("");
      });
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setAuthed(false);
    toast.success("Logged out");
  };

  if (!authed) {
    return (
      <AdminLogin
        onSuccess={(t) => {
          localStorage.setItem(STORAGE_KEY, t);
          setToken(t);
          setAuthed(true);
        }}
      />
    );
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: BG }}
      data-testid="admin-page"
    >
      <header
        className="sticky top-0 z-10 border-b"
        style={{ background: BG, borderColor: BORDER }}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1
            className="font-serif"
            style={{ fontSize: 22, letterSpacing: "-0.01em" }}
          >
            Admin <span style={{ color: GOLD }}>Console</span>
          </h1>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="text-xs uppercase tracking-widest hover:text-white transition-colors"
            style={{ color: SLATE }}
          >
            Sign out
          </button>
        </div>

        <nav className="max-w-5xl mx-auto px-6 pb-3 flex gap-2 overflow-x-auto">
          {[
            { id: "publish", label: "Publish Field Note" },
            { id: "leads", label: "Leads Inbox" },
            { id: "indexnow", label: "IndexNow Ping" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className="px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors whitespace-nowrap"
              style={{
                background: tab === t.id ? GOLD : "transparent",
                color: tab === t.id ? BG : SLATE,
                border: `1px solid ${tab === t.id ? GOLD : BORDER}`,
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {tab === "publish" && <PublishFieldNote token={token} />}
        {tab === "leads" && <LeadsInbox token={token} />}
        {tab === "indexnow" && <IndexNowPing token={token} />}
      </main>
    </div>
  );
}

/* ============================================================
   Login screen
   ============================================================ */
function AdminLogin({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setBusy(true);
    try {
      // Verify by hitting /api/leads (admin-gated)
      await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": pw } });
      onSuccess(pw);
      toast.success("Welcome back");
    } catch (err) {
      toast.error("Invalid admin token");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: BG }}
      data-testid="admin-login"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl p-8"
        style={{ background: CARD, border: `1px solid ${BORDER}` }}
      >
        <p
          className="mb-2 font-mono text-[10px] uppercase tracking-widest"
          style={{ color: GOLD }}
        >
          Restricted
        </p>
        <h2 className="font-serif text-white mb-6" style={{ fontSize: 26 }}>
          Admin Console
        </h2>
        <label
          htmlFor="admin-pw"
          className="block mb-2 text-xs uppercase tracking-widest"
          style={{ color: SLATE }}
        >
          Admin token
        </label>
        <input
          id="admin-pw"
          data-testid="admin-token-input"
          type="password"
          autoComplete="off"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="w-full rounded-lg px-3 py-3 text-white outline-none focus:ring-1"
          style={{
            background: "#0F0F13",
            border: `1px solid ${BORDER}`,
            fontSize: 14,
          }}
          placeholder="Enter admin token"
        />
        <button
          type="submit"
          disabled={busy || !pw.trim()}
          data-testid="admin-login-btn"
          className="mt-5 w-full rounded-full py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: GOLD, color: BG }}
        >
          {busy ? "Verifying..." : "Enter console"}
        </button>
      </form>
    </div>
  );
}

/* ============================================================
   Publish Field Note
   ============================================================ */
function PublishFieldNote({ token }) {
  const [form, setForm] = useState({
    slug: "",
    title: "",
    subtitle: "",
    kicker: "A field note",
    read_minutes: 4,
    is_published: true,
    bodyText: "",
  });
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Split body into blocks. Each blank-line-separated chunk = one block.
  // Lines wrapped in ""...""  or starting with "> " are treated as quote blocks.
  const bodyToBlocks = (raw) => {
    return raw
      .split(/\n\s*\n/)
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => {
        const isQuote =
          chunk.startsWith("> ") ||
          (chunk.startsWith('"') && chunk.endsWith('"'));
        return {
          type: isQuote ? "quote" : "p",
          text: chunk.replace(/^>\s*/, "").replace(/^"|"$/g, "").trim(),
        };
      });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.slug.trim() || !form.title.trim() || !form.bodyText.trim()) {
      toast.error("Slug, title, and body are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        kicker: form.kicker.trim() || "A field note",
        read_minutes: Number(form.read_minutes) || 4,
        is_published: form.is_published,
        body: bodyToBlocks(form.bodyText),
      };
      const { data } = await axios.post(`${API}/admin/field-notes`, payload, {
        headers: { "X-Admin-Token": token },
      });
      setLastResult(data);
      toast.success(
        data.created
          ? `Published: ${data.slug}`
          : `Updated: ${data.slug}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  // Save the current form as a DRAFT (is_published forced to false) and
  // open the preview page in a new tab. Lets the founder rehearse a note
  // fully rendered before flipping the toggle.
  const saveDraftAndPreview = async () => {
    if (!form.slug.trim() || !form.title.trim() || !form.bodyText.trim()) {
      toast.error("Slug, title, and body are required for a preview");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        kicker: form.kicker.trim() || "A field note",
        read_minutes: Number(form.read_minutes) || 4,
        is_published: false, // force draft
        body: bodyToBlocks(form.bodyText),
      };
      const { data } = await axios.post(`${API}/admin/field-notes`, payload, {
        headers: { "X-Admin-Token": token },
      });
      setLastResult(data);
      toast.success("Draft saved - opening preview");
      window.open(`/field-notes/${data.slug}?preview=1`, "_blank", "noopener");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-publish">
      <h2 className="font-serif text-white mb-1" style={{ fontSize: 24 }}>
        Publish a Field Note
      </h2>
      <p className="mb-8 text-sm" style={{ color: SLATE }}>
        Upserts by slug. Existing notes with the same slug are replaced. Blank
        lines separate paragraphs. Quote a block with a leading &gt; or wrap in
        double quotes.
      </p>

      <form onSubmit={submit} className="grid gap-5 md:grid-cols-2">
        <Field label="Slug (kebab-case)">
          <input
            data-testid="admin-note-slug"
            value={form.slug}
            onChange={(e) => update("slug", e.target.value)}
            placeholder="the-founder-who-didnt-notice"
            className="w-full rounded-lg px-3 py-3 text-white outline-none"
            style={{
              background: "#0F0F13",
              border: `1px solid ${BORDER}`,
              fontSize: 14,
            }}
          />
        </Field>

        <Field label="Kicker (small line above title)">
          <input
            data-testid="admin-note-kicker"
            value={form.kicker}
            onChange={(e) => update("kicker", e.target.value)}
            className="w-full rounded-lg px-3 py-3 text-white outline-none"
            style={{
              background: "#0F0F13",
              border: `1px solid ${BORDER}`,
              fontSize: 14,
            }}
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="Title">
            <input
              data-testid="admin-note-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className="w-full rounded-lg px-3 py-3 text-white outline-none"
              style={{
                background: "#0F0F13",
                border: `1px solid ${BORDER}`,
                fontSize: 14,
              }}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Subtitle (optional)">
            <input
              data-testid="admin-note-subtitle"
              value={form.subtitle}
              onChange={(e) => update("subtitle", e.target.value)}
              className="w-full rounded-lg px-3 py-3 text-white outline-none"
              style={{
                background: "#0F0F13",
                border: `1px solid ${BORDER}`,
                fontSize: 14,
              }}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Body (blank line = new paragraph, > line = quote)">
            <textarea
              data-testid="admin-note-body"
              value={form.bodyText}
              onChange={(e) => update("bodyText", e.target.value)}
              rows={14}
              className="w-full rounded-lg px-3 py-3 text-white outline-none font-mono leading-relaxed"
              style={{
                background: "#0F0F13",
                border: `1px solid ${BORDER}`,
                fontSize: 13,
              }}
              placeholder={`He built the firm on referrals. Now the referrals had stopped.\n\n> "Brother, the phone just went silent."\n\nHe was still writing the same LinkedIn posts. The world had moved on.`}
            />
          </Field>
        </div>

        <Field label="Read minutes">
          <input
            data-testid="admin-note-read"
            type="number"
            min={1}
            max={20}
            value={form.read_minutes}
            onChange={(e) => update("read_minutes", e.target.value)}
            className="w-full rounded-lg px-3 py-3 text-white outline-none"
            style={{
              background: "#0F0F13",
              border: `1px solid ${BORDER}`,
              fontSize: 14,
            }}
          />
        </Field>

        <label
          className="flex items-center gap-3 self-end mb-1"
          style={{ color: SLATE, fontSize: 13 }}
        >
          <input
            data-testid="admin-note-published"
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => update("is_published", e.target.checked)}
            className="w-4 h-4 accent-current"
            style={{ accentColor: GOLD }}
          />
          Published (visible on /field-notes)
        </label>

        <div className="md:col-span-2 flex flex-wrap items-center gap-4 mt-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-note-submit"
            className="rounded-full py-3 px-6 text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            style={{ background: GOLD, color: BG }}
          >
            {busy ? "Publishing..." : "Publish note"}
          </button>
          <button
            type="button"
            onClick={saveDraftAndPreview}
            disabled={busy}
            data-testid="admin-note-preview"
            className="rounded-full py-3 px-6 text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            style={{
              background: "transparent",
              color: GOLD,
              border: `1px solid ${GOLD}`,
            }}
          >
            Save draft &amp; preview
          </button>
          {lastResult && (
            <p className="text-xs" style={{ color: SLATE }}>
              Last save:{" "}
              <a
                href={`/field-notes/${lastResult.slug}${lastResult.note?.is_published === false ? "?preview=1" : ""}`}
                target="_blank"
                rel="noreferrer"
                className="underline"
                style={{ color: GOLD }}
                data-testid="admin-note-last-link"
              >
                /field-notes/{lastResult.slug}
                {lastResult.note?.is_published === false && " (draft)"}
              </a>
            </p>
          )}
        </div>
      </form>
    </section>
  );
}

/* ============================================================
   Leads Inbox
   ============================================================ */
function LeadsInbox({ token }) {
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { "X-Admin-Token": token },
      });
      setLeads(data);
    } catch (e) {
      setErr("Could not load leads");
    } finally {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section data-testid="admin-leads">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-serif text-white" style={{ fontSize: 24 }}>
          Leads Inbox{" "}
          <span className="text-sm ml-2" style={{ color: SLATE }}>
            ({leads.length})
          </span>
        </h2>
        <button
          onClick={load}
          data-testid="admin-leads-refresh"
          className="text-xs uppercase tracking-widest hover:text-white transition-colors"
          style={{ color: GOLD }}
        >
          Refresh
        </button>
      </div>

      {busy && <p style={{ color: SLATE }}>Loading...</p>}
      {err && <p className="text-red-400">{err}</p>}
      {!busy && !err && leads.length === 0 && (
        <p style={{ color: SLATE }}>No leads yet.</p>
      )}

      <div className="grid gap-3">
        {leads.map((l) => (
          <article
            key={l.id}
            data-testid="admin-lead-card"
            className="rounded-xl p-4 md:p-5"
            style={{ background: CARD, border: `1px solid ${BORDER}` }}
          >
            <header className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <p className="text-white text-sm font-semibold">
                  {l.name || "Unknown"}
                </p>
                <p className="text-xs" style={{ color: SLATE }}>
                  {l.business_name || "-"}
                </p>
              </div>
              <span
                className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
                style={{
                  background: "rgba(201,169,97,0.10)",
                  color: GOLD,
                  border: `1px solid ${BORDER}`,
                }}
              >
                {l.source || "letter"}
              </span>
            </header>
            <div
              className="mt-3 grid gap-1 text-xs"
              style={{ color: SLATE }}
            >
              <span>Email: <a href={`mailto:${l.email}`} className="hover:text-white transition-colors" style={{ color: "#EEE" }}>{l.email}</a></span>
              {l.phone && <span>Phone: {l.phone}</span>}
              {l.website && (
                <span>
                  Website:{" "}
                  <a
                    href={l.website.startsWith("http") ? l.website : `https://${l.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-white transition-colors"
                    style={{ color: GOLD }}
                  >
                    {l.website}
                  </a>
                </span>
              )}
              {l.country && <span>Country: {l.country}</span>}
              {l.challenge && (
                <p className="mt-2 leading-relaxed" style={{ color: "#DDD" }}>
                  {l.challenge}
                </p>
              )}
              <span className="mt-2 text-[10px]">
                {l.created_at ? new Date(l.created_at).toLocaleString() : ""}
              </span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   IndexNow Ping
   ============================================================ */
function IndexNowPing({ token }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const ping = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/indexnow-ping`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      setResult(data);
      toast.success(`Submitted ${data.submitted} URLs to IndexNow`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ping failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-indexnow">
      <h2 className="font-serif text-white mb-1" style={{ fontSize: 24 }}>
        IndexNow Ping
      </h2>
      <p className="mb-6 text-sm" style={{ color: SLATE }}>
        Submits every URL in sitemap.xml to Bing, Yandex, Seznam, and Naver in
        one call. Use it after publishing a new Field Note.
      </p>
      <button
        onClick={ping}
        disabled={busy}
        data-testid="admin-indexnow-btn"
        className="rounded-full py-3 px-6 text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-50"
        style={{ background: GOLD, color: BG }}
      >
        {busy ? "Pinging..." : "Ping IndexNow now"}
      </button>

      {result && (
        <pre
          className="mt-6 rounded-xl p-4 text-xs overflow-auto"
          style={{
            background: CARD,
            border: `1px solid ${BORDER}`,
            color: SLATE,
            maxHeight: 300,
          }}
          data-testid="admin-indexnow-result"
        >
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </section>
  );
}

/* ============================================================
   Utility
   ============================================================ */
function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block mb-2 text-xs uppercase tracking-widest"
        style={{ color: SLATE }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
